import React, { Component, ReactNode } from 'react';
import { API_BASE_URL, ENABLE_DETAILED_ERRORS, ENABLE_CONSOLE_LOGS, DEBUG_MODE } from '../lib/api';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error & {
    stack?: string;
    path?: string;
    method?: string;
    timestamp?: string;
    requestId?: string;
    statusCode?: number;
  };
  errorInfo?: React.ErrorInfo;
  copySuccess: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  private errorCount: number = 0;
  private maxErrors: number = 10;
  private lastReportTime: number = 0;
  private reportCooldownMs: number = 30000;
  private copySuccessTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, copySuccess: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error: error as State['error'],
      errorInfo: undefined,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.errorCount++;

    if (ENABLE_CONSOLE_LOGS || DEBUG_MODE) {
      console.group('🔴 ErrorBoundary caught an error');
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Error Count:', `${this.errorCount}/${this.maxErrors}`);
      console.groupEnd();
    }

    this.reportErrorToServer(error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.children !== this.props.children) {
      this.errorCount = 0;
    }
  }

  componentWillUnmount() {
    if (this.copySuccessTimer) clearTimeout(this.copySuccessTimer);
  }

  private reportErrorToServer(error: Error, errorInfo: React.ErrorInfo) {
    const now = Date.now();
    if (now - this.lastReportTime < this.reportCooldownMs) return;
    this.lastReportTime = now;

    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorCount: this.errorCount,
    };

    const errorReportUrl = `${API_BASE_URL}/logging/client-error`;
    const blob = new Blob([JSON.stringify(errorData)], { type: 'application/json' });
    const sent = navigator.sendBeacon(errorReportUrl, blob);

    if (!sent) {
      fetch(errorReportUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData),
        keepalive: true,
      }).catch(() => {
        // Silent — error reporting must not disrupt UX
      });
    }
  }

  private handleReload = () => {
    this.errorCount = 0;
    window.location.reload();
  };

  private handleRetry = () => {
    this.errorCount = 0;
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, copySuccess: false });
  };

  private copyErrorToClipboard = () => {
    const error = this.state.error;
    if (!error) return;

    const errorText = [
      `Error: ${error.message}`,
      `Stack: ${error.stack || 'No stack trace'}`,
      `URL: ${window.location.href}`,
      `Time: ${error.timestamp || new Date().toISOString()}`,
      `Request ID: ${error.requestId || 'N/A'}`,
    ].join('\n');

    const doSetCopied = () => {
      this.setState({ copySuccess: true });
      if (this.copySuccessTimer) clearTimeout(this.copySuccessTimer);
      this.copySuccessTimer = setTimeout(() => this.setState({ copySuccess: false }), 2500);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(errorText).then(doSetCopied).catch(() => {
        this.fallbackCopy(errorText, doSetCopied);
      });
    } else {
      this.fallbackCopy(errorText, doSetCopied);
    }
  };

  private fallbackCopy(text: string, onSuccess: () => void) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      onSuccess();
    } catch {
      // Copy not supported — nothing we can do silently
    } finally {
      document.body.removeChild(textarea);
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const error = this.state.error;
    // The APP_DEBUG master switch (ENABLE_DETAILED_ERRORS / DEBUG_MODE) forces the
    // detailed developer view even in a production build so operators can diagnose
    // live issues; otherwise fall back to the minimal user-friendly screen.
    const showDetails = ENABLE_DETAILED_ERRORS || DEBUG_MODE;

    // Non-debug: minimal, user-friendly fallback — never expose stack traces
    if (!showDetails) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-civic-500 to-civic-600 px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-full">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Something went wrong</h1>
                  <p className="text-civic-100 text-sm">We're working on it</p>
                </div>
              </div>
            </div>
            <div className="p-8 space-y-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                An unexpected error occurred. Our team has been notified. Please try reloading
                the page — if the problem persists, contact support.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleReload}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-civic-500 text-white rounded-xl hover:bg-civic-600 transition-all font-semibold shadow focus:outline-none focus:ring-2 focus:ring-civic-400 focus:ring-offset-2"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reload Page
                </button>
                <button
                  onClick={this.handleRetry}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
              <p className="text-xs text-center text-gray-400">
                Need help?{' '}
                <a
                  href="/contact"
                  className="text-civic-600 hover:underline focus:outline-none focus:underline"
                >
                  Contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Development / debug: detailed error view
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-full">
                <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Something went wrong
                </h1>
                <p className="text-green-100 text-sm">
                  Development error view
                  {this.errorCount > 1 && ` · Error #${this.errorCount}`}
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            {/* Error Message */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <svg className="h-5 w-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-800 font-semibold">Error</p>
              </div>
              <p className="text-red-700 font-medium text-sm font-mono break-words">
                {error?.message || 'An unexpected error occurred'}
              </p>
            </div>

            {/* Stack Trace */}
            {showDetails && error?.stack && (
              <div className="bg-gray-900 rounded-xl p-5 overflow-auto max-h-72">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-mono">STACK TRACE</span>
                  <button
                    onClick={this.copyErrorToClipboard}
                    className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600 transition-colors focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    {this.state.copySuccess ? '✓ Copied' : 'Copy Error'}
                  </button>
                </div>
                <pre className="text-green-400 text-xs whitespace-pre-wrap font-mono leading-relaxed">
                  {error.stack}
                </pre>
              </div>
            )}

            {/* Request Context */}
            {showDetails && (error?.path || error?.method || error?.timestamp || error?.requestId) && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <p className="text-blue-800 font-semibold mb-3 text-sm">Request Context</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {error?.method && (
                    <><span className="text-gray-500">Method:</span><span className="text-gray-900 font-mono font-semibold">{error.method}</span></>
                  )}
                  {error?.path && (
                    <><span className="text-gray-500">Path:</span><span className="text-gray-900 font-mono font-semibold">{error.path}</span></>
                  )}
                  {error?.statusCode && (
                    <><span className="text-gray-500">Status:</span><span className={`font-mono font-semibold ${error.statusCode >= 500 ? 'text-red-600' : 'text-yellow-600'}`}>{error.statusCode}</span></>
                  )}
                  {error?.timestamp && (
                    <><span className="text-gray-500">Time:</span><span className="text-gray-900 font-mono text-xs">{error.timestamp}</span></>
                  )}
                  {error?.requestId && (
                    <><span className="text-gray-500">Request ID:</span><span className="text-gray-900 font-mono text-xs">{error.requestId}</span></>
                  )}
                </div>
              </div>
            )}

            {/* Component Stack */}
            {showDetails && this.state.errorInfo?.componentStack && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                <p className="text-purple-800 font-semibold mb-3 text-sm">Component Stack</p>
                <pre className="text-purple-700 text-xs whitespace-pre-wrap font-mono max-h-48 overflow-auto">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            {/* Debug Info */}
            {DEBUG_MODE && (
              <div className="bg-gray-100 rounded-xl p-5">
                <p className="text-gray-800 font-semibold mb-3 text-sm">Debug Information</p>
                <div className="grid grid-cols-2 gap-3 text-sm font-mono">
                  <span className="text-gray-500">URL:</span>
                  <span className="text-gray-900 text-xs break-all">{window.location.href}</span>
                  <span className="text-gray-500">Error Count:</span>
                  <span className="text-gray-900">{this.errorCount}</span>
                  <span className="text-gray-500">Timestamp:</span>
                  <span className="text-gray-900 text-xs">{new Date().toISOString()}</span>
                </div>
              </div>
            )}

            {/* Help Text */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 font-medium text-sm mb-1">Need Help?</p>
              <ul className="text-amber-700 text-xs space-y-1">
                <li>• Check the browser console (F12) for more details</li>
                <li>• Reload the page and try again</li>
                <li>• If the problem persists, contact support</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold shadow-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reload Page
              </button>
              <button
                onClick={this.handleRetry}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Try Again
              </button>
              {showDetails && (
                <button
                  onClick={this.copyErrorToClipboard}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-all font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                  aria-live="polite"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  {this.state.copySuccess ? 'Copied!' : 'Copy Error'}
                </button>
              )}
            </div>
          </div>

          {showDetails && (
            <div className="px-8 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Swachh Bharat Public Portal — Dev Mode</span>
                <span>v{import.meta.env.VITE_APP_VERSION || '1.0.0'}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
